import { useCallback, useEffect, useRef, useState } from 'react';
import { Mail, MessageCircle, ClipboardList, FileSignature, ScrollText, Megaphone, Copy, Loader2, Wand2, Trash2, History, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { aiAPI } from '@/lib/api';
import { streamAI } from '@/lib/aiStream';
import { MarkdownText } from './MarkdownText';
import { RecordPicker } from './RecordPicker';

export const WRITER_TASKS = [
  { id: 'email', label: 'Follow-up email', icon: Mail, needsRecord: true },
  { id: 'whatsapp', label: 'WhatsApp message', icon: MessageCircle, needsRecord: true },
  { id: 'summary', label: 'Profile summary', icon: ClipboardList, needsRecord: true },
  { id: 'sop', label: 'SOP', icon: ScrollText, needsRecord: true },
  { id: 'cover_letter', label: 'Cover letter', icon: FileSignature, needsRecord: true },
  { id: 'social_post', label: 'Social post', icon: Megaphone, needsRecord: false },
];
const TASK_LABEL = Object.fromEntries(WRITER_TASKS.map((t) => [t.id, t.label]));
const TONES = ['Friendly', 'Professional', 'Persuasive', 'Formal', 'Urgent'];
const PLATFORMS = ['Facebook', 'Instagram', 'LinkedIn', 'TikTok', 'X (Twitter)'];

const TaskGrid = ({ task, setTask }) => (
  <div className="grid grid-cols-2 gap-2" data-testid="ai-writer-task-grid">
    {WRITER_TASKS.map(({ id, label, icon: Icon }) => (
      <button
        key={id}
        type="button"
        data-testid={`ai-writer-task-${id}`}
        onClick={() => setTask(id)}
        className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-colors ${task === id ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-200 hover:border-teal-400'}`}
      >
        <Icon className="w-4 h-4 shrink-0" /> <span className="truncate">{label}</span>
      </button>
    ))}
  </div>
);

const DraftHistory = ({ drafts, onOpen, onDelete }) => (
  <div className="mt-6" data-testid="ai-writer-history">
    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-1"><History className="w-4 h-4" /> Recent drafts</h4>
    {drafts.length === 0 && <p className="text-xs text-gray-500">Your generated drafts will appear here.</p>}
    <div className="space-y-1 max-h-56 overflow-y-auto">
      {drafts.map((d) => (
        <div key={d.id} data-testid={`ai-draft-${d.id}`} onClick={() => onOpen(d)} className="group flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700">
          <span className="text-xs font-medium text-teal-700 dark:text-teal-300 w-28 shrink-0 truncate">{TASK_LABEL[d.task]}</span>
          <span className="text-xs text-gray-600 dark:text-gray-300 truncate flex-1">{d.record_name || d.content.slice(0, 60)}</span>
          <button type="button" data-testid={`ai-draft-delete-${d.id}`} onClick={(e) => { e.stopPropagation(); onDelete(d.id); }} className="opacity-60 lg:opacity-0 group-hover:opacity-100 p-1">
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        </div>
      ))}
    </div>
  </div>
);

export const AIWriterPanel = ({ disabled }) => {
  const [task, setTask] = useState('email');
  const [record, setRecord] = useState(null);
  const [tone, setTone] = useState('Friendly');
  const [platform, setPlatform] = useState('Instagram');
  const [instructions, setInstructions] = useState('');
  const [output, setOutput] = useState('');
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const abortRef = useRef(null);
  const meta = WRITER_TASKS.find((t) => t.id === task);

  const loadDrafts = useCallback(async () => {
    try { setDrafts((await aiAPI.getDrafts()).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const generate = async () => {
    if (meta.needsRecord && !record && !instructions.trim()) {
      toast.error('Pick a lead/student or add instructions');
      return;
    }
    setBusy(true);
    setOutput('');
    abortRef.current = new AbortController();
    try {
      await streamAI('/ai/generate', {
        task, record_type: record?.record_type, record_id: record?.id,
        instructions: instructions.trim() || undefined, tone,
        platform: task === 'social_post' ? platform : undefined,
      }, (ev) => {
        if (ev.type === 'delta') setOutput((o) => o + ev.content);
        else if (ev.type === 'error') toast.error(ev.message);
      }, abortRef.current.signal);
    } catch (e) {
      if (e.name !== 'AbortError') toast.error(e.message);
    }
    setBusy(false);
    loadDrafts();
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(output); toast.success('Copied to clipboard'); } catch { toast.error('Copy failed'); }
  };

  const deleteDraft = async (id) => {
    try { await aiAPI.deleteDraft(id); loadDrafts(); } catch { toast.error('Could not delete draft'); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="ai-writer-panel">
      <div className="lg:col-span-2 space-y-4">
        <TaskGrid task={task} setTask={setTask} />
        {meta.needsRecord && (
          <div className="space-y-1.5">
            <Label>Lead / student / application</Label>
            <RecordPicker value={record} onChange={setRecord} testId="ai-writer-record" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Tone</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger data-testid="ai-writer-tone"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {task === 'social_post' && (
            <div className="space-y-1.5">
              <Label>Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="ai-writer-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Extra instructions (optional)</Label>
          <Textarea
            data-testid="ai-writer-instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={3}
            placeholder={task === 'social_post' ? 'e.g. September 2026 UK intake, scholarships up to £5,000' : 'e.g. Remind them about the IELTS deadline next week'}
          />
        </div>
        {busy ? (
          <Button data-testid="ai-writer-stop-btn" variant="outline" onClick={() => abortRef.current?.abort()} className="w-full">
            <Square className="w-4 h-4 mr-1" /> Stop
          </Button>
        ) : (
          <Button data-testid="ai-writer-generate-btn" disabled={disabled} onClick={generate} className="w-full bg-teal-600 hover:bg-teal-700 text-white">
            <Wand2 className="w-4 h-4 mr-1" /> Generate: {meta.label}
          </Button>
        )}
        <DraftHistory drafts={drafts} onOpen={(d) => { setTask(d.task); setOutput(d.content); }} onDelete={deleteDraft} />
      </div>
      <div className="lg:col-span-3">
        <div className="rounded-2xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/40 min-h-[420px] flex flex-col">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-slate-600">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin text-teal-600" />} {meta.label}
            </span>
            <Button data-testid="ai-writer-copy-btn" variant="ghost" size="sm" disabled={!output || busy} onClick={copy}>
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </div>
          <div className="p-4 flex-1 overflow-y-auto max-h-[640px]" data-testid="ai-writer-output">
            {output ? <MarkdownText>{output}</MarkdownText> : (
              <p className="text-sm text-gray-400">Pick what to write, choose a record and press Generate. Review before sending — nothing is sent automatically.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
